import { MAX_PAGE_SIZE } from "@cpg/common";

// CSV export helper shared by every list page's "Export CSV" button
// (DataTable's `csvExport` prop only renders/wires the button — this is
// where the actual export logic lives, per the plan). Never touches
// domain-specific formatting: callers pass a `value(row) => string` per
// column, already formatted the way that page's table renders it.

/** Hard cap on exported rows, independent of the reported `total` — see exportCsv. */
export const CSV_EXPORT_MAX_ROWS = 5_000;

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string;
}

export interface ExportCsvOptions<T> {
  columns: CsvColumn<T>[];
  /** Re-run of the current list query at MAX_PAGE_SIZE, one page at a time. */
  fetchPage: (page: number, pageSize: number) => Promise<{ data: T[]; total: number }>;
  /** e.g. 'transactions' -> 'transactions-2026-07-31.csv'. */
  filenamePrefix: string;
}

/**
 * Wraps a field in quotes and doubles embedded quotes if it contains a comma,
 * quote, or newline. Also neutralizes CSV/Excel formula injection: a leading
 * `=`/`+`/`-`/`@`/tab/CR gets prefixed with a single quote `'`, the standard
 * Excel/Sheets "treat as text, not formula" escape — it doesn't change the
 * visible cell content in spreadsheet apps and doesn't affect any other CSV
 * consumer (it's plain CSV data, not special syntax). Several exports
 * include free-text fields set by merchants on public self-registration
 * (e.g. `merchantBusinessName`), so this can't be skipped.
 */
function escapeCsvField(value: string): string {
  const safe = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  if (/[",\r\n]/.test(safe)) {
    return `"${safe.replace(/"/g, '""')}"`;
  }
  return safe;
}

function toCsvRow(values: string[]): string {
  return values.map(escapeCsvField).join(",");
}

function todayStamp(): string {
  return new Date().toISOString().slice(0, 10);
}

function triggerDownload(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Re-runs the current list query at MAX_PAGE_SIZE per page, starting from
 * page 1, accumulating rows, and stopping at whichever comes first: the
 * reported `total`, or CSV_EXPORT_MAX_ROWS (5000) — if `total` exceeds
 * 5000, exports exactly the first 5000 rows, no UI warning (not asked for
 * in this task).
 */
export async function exportCsv<T>(options: ExportCsvOptions<T>): Promise<void> {
  const { columns, fetchPage, filenamePrefix } = options;

  const rows: T[] = [];
  let page = 1;
  let total = Number.POSITIVE_INFINITY;

  while (rows.length < total && rows.length < CSV_EXPORT_MAX_ROWS) {
    const result = await fetchPage(page, MAX_PAGE_SIZE);
    total = result.total;
    if (result.data.length === 0) break; // guard against an infinite loop if `total` is misreported
    rows.push(...result.data);
    page += 1;
  }

  const capped = rows.slice(0, CSV_EXPORT_MAX_ROWS);

  const lines = [
    toCsvRow(columns.map((column) => column.header)),
    ...capped.map((row) => toCsvRow(columns.map((column) => column.value(row)))),
  ];

  triggerDownload(lines.join("\r\n"), `${filenamePrefix}-${todayStamp()}.csv`);
}
